import * as Api from '@ViewModels';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Button } from '../../../../../../components-v2/Button';
import { DateRangePicker } from '../../DateRangePicker';
import { styleSheet } from './styles';

interface ActivityLogTableProps {
	data: Api.IBoardItemActivity[];
	isLoading?: boolean;
	hasNextPage?: boolean;
	onLoadMore?: () => void;
	isLoadingMore?: boolean;
	activityDateFrom: string;
	activityDateTo: string;
	onActivityDateChange: (from: string, to: string) => void;
	boardId: string | null;
}

export const ActivityLogTable: React.FC<ActivityLogTableProps> = ({
	data,
	isLoading,
	hasNextPage,
	onLoadMore,
	isLoadingMore,
	activityDateFrom,
	activityDateTo,
	onActivityDateChange,
	boardId,
}) => {
	const columns = React.useMemo(
		(): ColumnDef<Api.IBoardItemActivity>[] => [
			{
				id: 'activityDate',
				accessorKey: 'creationDate',
				header: 'Activity Date',
				cell: ({ getValue }) => {
					const val = getValue<string>();
					return val ? moment(val).format('MM/DD/YYYY') : '—';
				},
			},
			{
				id: 'activityBy',
				header: 'Activity By',
				cell: ({ row }) => {
					const creator = row.original.creator;
					if (!creator) {
						return '—';
					}
					return [creator.firstName, creator.lastName].filter(Boolean).join(' ') || '—';
				},
			},
			{
				id: 'eventType',
				accessorKey: 'eventType',
				header: 'Event Type',
			},
			{
				id: 'description',
				accessorKey: 'text',
				header: 'Description',
			},
			{
				id: 'from',
				header: 'From',
				cell: ({ row }) => row.original.change?.fromStage?.name ?? '—',
			},
			{
				id: 'to',
				header: 'To',
				cell: ({ row }) => row.original.change?.toStage?.name ?? '—',
			},
		],
		[boardId]
	);

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<h3 className={css(styleSheet.title)}>Activity Log</h3>
				<div className={css(styleSheet.headerFilters)}>
					<DateRangePicker
						size='sm'
						ariaLabel='Activity date range'
						from={activityDateFrom}
						to={activityDateTo}
						onChange={onActivityDateChange}
					/>
				</div>
			</div>
			{isLoading && data.length === 0 ? (
				<p className={css(styleSheet.emptyState)}>Loading activity…</p>
			) : data.length === 0 ? (
				<div className={css(styleSheet.emptyStateFiltered)}>
					<p className={css(styleSheet.emptyStateFilteredTitle)}>No results found</p>
					<p className={css(styleSheet.emptyStateFilteredMessage)}>
						No activity matches your current filters. Try adjusting or clearing them.
					</p>
				</div>
			) : (
				<div className={css(styleSheet.tableWrapper)}>
					<table className={css(styleSheet.table)}>
						<thead>
							{table.getHeaderGroups().map(hg => (
								<tr key={hg.id}>
									{hg.headers.map(h => (
										<th key={h.id} className={css(styleSheet.th)}>
											{h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
										</th>
									))}
								</tr>
							))}
						</thead>
						<tbody>
							{table.getRowModel().rows.map(row => (
								<tr key={row.id} className={css(styleSheet.tr)}>
									{row.getVisibleCells().map(cell => (
										<td key={cell.id} className={css(styleSheet.td)}>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{hasNextPage && onLoadMore ? (
				<Button variant='secondary' onClick={onLoadMore} isLoading={isLoadingMore}>
					Load More
				</Button>
			) : null}
		</div>
	);
};
